import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatINR, fmtDate } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Home, Droplets, Wallet, Receipt, ArrowRight, Loader2, AlertCircle } from "lucide-react";

export default function CitizenDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/citizen").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return null;

  const { properties, water_consumers, demands, recent_payments, kpis } = data;
  const pendingDemands = demands.filter((d) => ["pending", "partial", "overdue"].includes(d.status));

  return (
    <div className="space-y-8" data-testid="citizen-dashboard">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#E8702A] font-semibold">Welcome</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white mt-1">
          Namaste, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-slate-500 mt-1">Manage your properties, dues and civic services.</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {[
          { label: "Properties", value: kpis.properties_count, icon: Home, tone: "bg-[#0F2942]" },
          { label: "Water Connections", value: kpis.connections_count, icon: Droplets, tone: "bg-[#0891B2]" },
          { label: "Pending Dues", value: formatINR(kpis.pending_dues), icon: AlertCircle, tone: "bg-[#E8702A]" },
          { label: "Lifetime Paid", value: formatINR(kpis.lifetime_paid), icon: Wallet, tone: "bg-[#0D7650]" },
        ].map((k) => (
          <Card key={k.label} className="hover-lift bg-white/85 backdrop-blur-sm" data-testid={`citizen-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
              <div className={`w-9 h-9 rounded ${k.tone} flex items-center justify-center mb-3`}>
                <k.icon className="w-4 h-4 text-white" strokeWidth={1.75} />
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-300">{k.label}</div>
              <div className="text-xl font-heading font-bold tabular-nums text-slate-900 dark:text-white mt-1">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending dues */}
      <Card data-testid="citizen-pending-dues">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading">Pending dues</CardTitle>
          <Link to="/portal/pay"><Button size="sm" className="bg-[#E8702A] hover:bg-[#d95f1c]" data-testid="btn-pay-all">Pay dues <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </CardHeader>
        <CardContent>
          {pendingDemands.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center border rounded-lg">No pending dues. You are all clear!</div>
          ) : (
            <div className="divide-y">
              {pendingDemands.slice(0, 5).map((d) => (
                <div key={d.id} className="py-3 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium">{d.demand_id} · <span className="capitalize">{d.module.replace("_", " ")}</span></div>
                    <div className="text-xs text-slate-500">Period {d.period} · Due {fmtDate(d.due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold">{formatINR(d.total - (d.paid || 0))}</div>
                    <Badge variant={d.status === "overdue" ? "destructive" : "secondary"} className="mt-1 capitalize">{d.status}</Badge>
                  </div>
                  <Link to={`/portal/pay/${d.id}`}>
                    <Button size="sm" data-testid={`btn-pay-${d.id}`}>Pay</Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My properties */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="citizen-my-properties">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading">My Properties</CardTitle>
            <Link to="/properties" className="text-xs text-[#0F2942] hover:underline">View all</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {properties.length === 0 && <div className="text-sm text-slate-500 p-4 text-center">No properties linked yet.</div>}
            {properties.slice(0, 4).map((p) => (
              <Link key={p.id} to={`/properties/${p.id}`} className="block hover-lift p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{p.property_id}</div>
                    <div className="text-xs text-slate-500 truncate">{p.address}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Dues</div>
                    <div className="text-sm font-semibold tabular-nums">{formatINR(p.total_dues)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="citizen-recent-payments">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading">Recent Payments</CardTitle>
            <Link to="/portal/receipts" className="text-xs text-[#0F2942] hover:underline">All receipts</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {recent_payments.length === 0 && <div className="text-sm text-slate-500 p-4 text-center">No payments yet.</div>}
            {recent_payments.slice(0, 5).map((p) => (
              <Link key={p.id} to={`/receipts/${p.receipt_number || p.payment_id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded bg-[#0D7650]/10 flex items-center justify-center">
                    <Receipt className="w-4 h-4 text-[#0D7650]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{p.receipt_number || p.payment_id}</div>
                    <div className="text-xs text-slate-500">{fmtDate(p.completed_at)} · {p.method.toUpperCase()}</div>
                  </div>
                </div>
                <div className="font-mono text-sm font-semibold">{formatINR(p.amount)}</div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
